'use client';
import { trackEvent as browserTrackEvent } from '#pplx/frontend/analytics/browser.js';
import { resolveAskInstrumentationSource } from '#pplx/frontend/apps/ask/analytics/AskInstrumentationTransportSupport.js';
import { blocklistedOrgMiddleware } from '#pplx/frontend/apps/ask/analytics/blocklistedOrgMiddleware.js';
import { unscopedDefaultsMiddleware } from '#pplx/frontend/apps/ask/analytics/unscopedDefaultsMiddleware.js';
import { getBootstrapConfig } from '#pplx/frontend/apps/ask/bootstrap-config/bootstrapConfig.js';
import { CONNECTOR_OAUTH_SCOPES_SAVED_EVENT_NAME, CONNECTOR_OAUTH_SCOPE_LIST_TOGGLED_EVENT_NAME, CONNECTOR_OAUTH_SCOPES_BULK_CHANGED_EVENT_NAME, CONNECTOR_OAUTH_SCOPE_TOGGLED_EVENT_NAME, } from '#pplx/frontend/apps/ask/events/connectors/analytics.js';
import { getAnalyticsQuerySource } from '#pplx/frontend/apps/ask/events/getAnalyticsQuerySource.js';
import { getAnalyticsSource } from '#pplx/frontend/apps/ask/events/getAnalyticsSource.js';
import { INCOGNITO_TOGGLED_EVENT_NAME } from '#pplx/frontend/apps/ask/events/sidebar/analytics.js';
import { useAnalyticsUser } from '#pplx/frontend/apps/ask/events/useAnalyticsUser.js';
import { useSession } from '#pplx/frontend/auth/useSession.js';
import * as BUILD_CAPABILITIES from '#pplx/frontend/deployment-capabilities/build.js';
import { composeInstrumentationMiddleware } from '#pplx/frontend/framework/instrumentation/composeInstrumentationMiddleware.js';
import { NOOP_INSTRUMENTATION_CONFIG } from '#pplx/frontend/framework/instrumentation/types.js';
import { useInstrumentation } from '#pplx/frontend/framework/instrumentation/useInstrumentation.js';
import { logger } from '#pplx/frontend/logger/browser.js';
import { useWebPlatform } from '#pplx/frontend/platform-components/useWebPlatform.js';
import { useLayoutEffect, useMemo } from 'react';
const ASK_TRANSPORT_NAMESPACE = 'ask';
const PRIVACY_SAFE_EVENT_LOCATIONS = new Map([
    [INCOGNITO_TOGGLED_EVENT_NAME, { url: '/incognito', referrer: '' }],
    // Custom connector selectors can contain user-authored names on any setup surface.
    [CONNECTOR_OAUTH_SCOPES_SAVED_EVENT_NAME, { url: '/connectors', referrer: '' }],
    [CONNECTOR_OAUTH_SCOPE_LIST_TOGGLED_EVENT_NAME, { url: '/connectors', referrer: '' }],
    [CONNECTOR_OAUTH_SCOPES_BULK_CHANGED_EVENT_NAME, { url: '/connectors', referrer: '' }],
    [CONNECTOR_OAUTH_SCOPE_TOGGLED_EVENT_NAME, { url: '/connectors', referrer: '' }],
]);
/**
 * Installs the ask transport into the framework instrumentation root while
 * mounted. Events from blocklisted organizations are dropped before the
 * unscoped-default sanitizer, and survivors forward with `user`/`source`
 * enrichment. Which surfaces emit is decided per call site by
 * `useInstrumentation`, not by a pathname filter here.
 */
export function AskInstrumentationTransport() {
    const { session, status: sessionStatus } = useSession();
    const { device: { isWindowsApp, isLinuxOS, isMacOS }, } = useWebPlatform();
    const { configure } = useInstrumentation();
    const bootstrapConfigErp = getBootstrapConfig().erp;
    const analyticsSource = getAnalyticsSource(isWindowsApp, isLinuxOS, bootstrapConfigErp ?? undefined, isMacOS);
    const queryAnalyticsSource = getAnalyticsQuerySource(isWindowsApp, bootstrapConfigErp, isLinuxOS, isMacOS);
    const analyticsUser = useAnalyticsUser(session);
    const send = useMemo(() => composeInstrumentationMiddleware({
        terminal: (event) => {
            browserTrackEvent({
                name: event.name,
                data: event.data,
                source: resolveAskInstrumentationSource(event.name, analyticsSource, queryAnalyticsSource),
                user: analyticsUser,
                overrides: PRIVACY_SAFE_EVENT_LOCATIONS.get(event.name),
            });
        },
        middleware: [blocklistedOrgMiddleware, unscopedDefaultsMiddleware],
        getContext: () => ({ analyticsUser }),
        onError: (error, index) => logger.error('ask instrumentation middleware failed', { error, index }),
    }), [analyticsSource, analyticsUser, queryAnalyticsSource]);
    useLayoutEffect(() => {
        configure({
            name: ASK_TRANSPORT_NAMESPACE,
            send,
            enabledState: !BUILD_CAPABILITIES.productAnalytics
                ? { enabled: false, reason: 'dormant' }
                : sessionStatus === 'loading'
                    ? { enabled: false, reason: 'session-loading' }
                    : { enabled: true },
        });
        return () => configure(NOOP_INSTRUMENTATION_CONFIG);
    }, [configure, send, sessionStatus]);
    return null;
}
//# sourceMappingURL=AskInstrumentationTransport.js.map